import { ThemeStore, useThemeStore } from '@/store/theme'
import { FormEvent, useRef, useState, useEffect, useMemo } from 'react'
import { Heart, Menu, Search, Clock10, Tag, ShoppingBag, UserCircle2, X, ArrowLeft, Handbag, CalendarDays } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import Nav from './Nav'
import { useAuthStore } from '@/store/authStore'
import { useQuery } from '@apollo/client/react'
import { useNavigate, useLocation, NavLink } from 'react-router-dom'
import { GET_ACTIVE_CUSTOMER_QUERY } from '@/api/getActiveCustomer'
import { GET_PRODUCTS_BADGES_QUERY } from '@/api/getProductsBadges'
import { SEARCH_PRODUCTS_QUERY } from '@/api/searchProducts'
import { GET_HEADER_QUERY } from '@/api/getHeader'
import { GET_ACTIVE_ORDER_ID_QUERY } from '@/api/cart'
import { NavHeaderLinkItem, NavLinkItemValue } from '@/gql/graphql'
import { HORIZONTAL_MENU_CODE, MAIN_MENU_CODE } from '@/utils/navigation'
import { useChannelStore } from '@/store/channel'
import Container from '@/components/Container'

export interface HeaderProps {
  onNavigateToAccount?: () => void
  onNavigateToCart?: () => void
}

export default function Header({ onNavigateToAccount, onNavigateToCart }: HeaderProps = {}) {
  const { t } = useTranslation()
  const [logoError, setLogoError] = useState<boolean>(false)
  const [isNavOpen, setIsNavOpen] = useState<boolean>(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('')
  const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false)
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    const stored = localStorage.getItem('recentSearches')
    if (stored) {
      try {
        return JSON.parse(stored)
      } catch (e) {
        console.error('Failed to parse recent searches', e)
      }
    }
    return []
  })
  const isSearching = searchQuery.trim().length > 0 || isSearchFocused
  const headerRef = useRef<HTMLElement>(null)

  const tenantTheme = useThemeStore((store: ThemeStore) => store.tenantTheme)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const pricesIncludeTax = useChannelStore((s) => s.pricesIncludeTax)
  const navigate = useNavigate()
  const location = useLocation()

  const { data: mainMenuData } = useQuery(GET_HEADER_QUERY, {
    variables: {
      code: MAIN_MENU_CODE
    }
  })

  const mainMenuNavLinks: Pick<NavHeaderLinkItem, 'link' | 'children' | 'label'>[] = useMemo(() => {

    return mainMenuData?.getHeader?.navLinks
      .filter((link: NavHeaderLinkItem) => link.active)
      .map((link: NavHeaderLinkItem) => ({
        label: link.label,
        link: link.link,
        children: link.children?.filter((child: NavLinkItemValue) => child.active)
      })) || []
  }, [mainMenuData])

  const { data: horizontalMenuData } = useQuery(GET_HEADER_QUERY, {
    variables: {
      code: HORIZONTAL_MENU_CODE
    }
  })

  const horizontalMenuNavLinks: Pick<NavHeaderLinkItem, 'link' | 'children' | 'label'>[] = useMemo(() => {
    return horizontalMenuData?.getHeader?.navLinks
      .filter((link: NavHeaderLinkItem) => link.active)
      .map((link: NavHeaderLinkItem) => ({
        label: link.label,
        link: link.link,
        children: link.children?.filter((child: NavLinkItemValue) => child.active)
      })) || []
  }, [horizontalMenuData])

  const favoritesPath = isAuthenticated ? '/account?section=favorites' : '/favorites'

  const handleNavClick = (destiny: string, isExternal?: boolean) => {
    if (isExternal) {
      window.open(destiny, '_blank')
    } else {
      navigate(destiny)
    }
  }

  const { data: customerData } = useQuery(GET_ACTIVE_CUSTOMER_QUERY, {
    skip: !isAuthenticated
  })

  const { data: activeOrderData } = useQuery(GET_ACTIVE_ORDER_ID_QUERY, {
    fetchPolicy: 'cache-and-network'
  })

  const customer = customerData?.activeCustomer
  const cartItemCount = activeOrderData?.activeOrder?.totalQuantity || 0

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const saveRecentSearch = (term: string) => {
    const trimmed = term.trim()
    if (!trimmed) return

    setRecentSearches(prev => {
      let searches = [...prev]
      // Remove if already exists to move to top
      searches = searches.filter(s => s.toLowerCase() !== trimmed.toLowerCase())
      // Add to beginning
      searches.unshift(trimmed)
      // Keep only top 5
      searches = searches.slice(0, 5)

      localStorage.setItem('recentSearches', JSON.stringify(searches))
      return searches
    })
  }

  const { data: searchData, loading: isSearchLoading } = useQuery(SEARCH_PRODUCTS_QUERY, {
    variables: {
      input: {
        term: debouncedSearchQuery,
        take: 24,
        groupByProduct: true
      }
    },
    skip: !debouncedSearchQuery.trim(),
  })

  const searchResults = useMemo(() => searchData?.search?.items || [], [searchData?.search?.items])
  const totalSearchResults = searchData?.search?.totalItems || 0

  const productIds = useMemo(() => searchResults.slice(0, 5).map(item => item.productId), [searchResults])

  const { data: badgesQueryData } = useQuery(GET_PRODUCTS_BADGES_QUERY, {
    variables: { ids: productIds },
    skip: productIds.length === 0,
  })

  const productPricesMap = useMemo(() => {
    const map = new Map<string, number>()
    const mapWithTax = new Map<string, number>()
    if (!badgesQueryData?.products?.items) return { map, mapWithTax }

    badgesQueryData.products.items.forEach(product => {
      const firstVariant = product.variantList.items[0]
      if (firstVariant) {
        const minQuantity = firstVariant.customFields?.minQuantity ?? 1
        let priceToUse = firstVariant.price
        let priceToUseWithTax = firstVariant.priceWithTax

        const currentPriceObj = firstVariant.prices?.[0]
        if (currentPriceObj?.customFields?.quantityPriceTiers?.length) {
          const applicableTier = [...currentPriceObj.customFields.quantityPriceTiers]
            .reverse()
            .find(tier => tier.quantityStart != null && minQuantity >= tier.quantityStart)

          if (applicableTier && applicableTier.price != null) {
            priceToUse = applicableTier.price
            // Tax rate approximated from the variant's own net/gross ratio -
            // the custom-field tier struct doesn't carry its own priceWithTax.
            priceToUseWithTax = (firstVariant.price && firstVariant.priceWithTax)
              ? Math.round(applicableTier.price * (firstVariant.priceWithTax / firstVariant.price))
              : applicableTier.price
          }
        }
        map.set(product.id, priceToUse)
        mapWithTax.set(product.id, priceToUseWithTax)
      }
    })

    return { map, mapWithTax }
  }, [badgesQueryData])

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (searchQuery.trim()) {
      saveRecentSearch(searchQuery)
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`)
      setSearchQuery('')
      setIsSearchFocused(false)
    }
  }

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
  }

  const handleSearchBlur = () => {
    // Keep search open if there's a query, close after a delay to allow clicking results
    setTimeout(() => {
      if (!searchQuery.trim()) {
        setIsSearchFocused(false)
      }
    }, 200)
  }

  const shouldShowResults = isSearching
  const isAccountPage = location.pathname.startsWith('/account')

  const accountButtonLabel = isAuthenticated && customer ? (
    <>{t('header.greeting')} <span className="font-semibold">{customer.firstName} {customer.lastName}</span></>
  ) : (
    <>{t('header.login')} <span className="font-semibold">{t('header.loginBold')}</span></>
  )

  // The visible label is JSX, and on screens between md and lg it is wrapped in
  // `hidden lg:inline` — display:none is excluded from the accessible name, so
  // the button needs a plain-text copy of it.
  const accountButtonText = isAuthenticated && customer
    ? `${t('header.greeting')} ${customer.firstName} ${customer.lastName}`
    : `${t('header.login')} ${t('header.loginBold')}`

  const handleAccountClick = () => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    if (onNavigateToAccount) {
      onNavigateToAccount()
    } else {
      handleNavClick('Perfil')
    }
  }

  const formatPrice = (price: { __typename?: 'SinglePrice' | 'PriceRange', value?: number, min?: number, max?: number } | number | null | undefined, currencyCode: string) => {
    if (price === null || price === undefined) return ''

    // Check if price is an object with value or min properties
    let value = 0;
    let hasValue = false;
    if (typeof price === 'object') {
      if (price.__typename === 'SinglePrice' || 'value' in price) {
        value = price.value || 0;
        hasValue = true;
      } else if (price.__typename === 'PriceRange' || 'min' in price) {
        value = price.min || 0;
        hasValue = true;
      }
    } else if (typeof price === 'number') {
      value = price;
      hasValue = true;
    }

    if (!hasValue) return ''

    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: currencyCode || 'EUR',
      minimumFractionDigits: 2,
    }).format(value / 100)
  }

  return (
    <>
      <header ref={headerRef} className={`w-full bg-background border-b border-secondary/30 ${shouldShowResults || isNavOpen ? 'relative z-50' : 'relative'}`}>

        {/* Main header */}
        <Container className="py-4 md:pb-2 flex flex-col gap-3">
          {/* Desktop layout */}
          <div className="hidden md:flex items-center gap-6">
            {/* Logo */}
            <div className="flex items-center min-w-40">
              <div
                className="text-foreground font-bold text-2xl flex items-center gap-2 cursor-pointer"
              >
                <NavLink
                  to="/"
                  className="flex items-center gap-2 hover:text-secondary transition-colors cursor-pointer"
                >
                  {!logoError ? (
                    <img
                      src={tenantTheme.logoUrl}
                      alt={`${tenantTheme.name} logo`}
                      className="h-8"
                      onError={() => setLogoError(true)}
                      title={`${tenantTheme.name} logo`}
                      width={160}
                      height={32}
                    />
                  ) : (
                    <span className="font-bold text-xl">{tenantTheme.name}</span>
                  )}
                </NavLink>
              </div>
            </div>

            {/* Categories dropdown + Search bar */}
            <div className="flex items-center gap-2 flex-1">
              <button
                type="button"
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-secondary/20 bg-background text-foreground text-sm whitespace-nowrap hover:border-secondary/40 transition-colors cursor-pointer"
                onClick={() => handleNavClick('/categories')}
                aria-label={t('header.categories')}
              >
                <span className="font-medium">{t('header.categories')}</span>
                {/* <ChevronDown className="w-4 h-4 text-secondary" /> */}
              </button>

              <form onSubmit={handleSearchSubmit} className="flex-1 flex items-center">
                <div className="flex items-center gap-2 w-full rounded-full border border-secondary/20 bg-background px-4 py-2">
                  <Search className="w-4 h-4 text-secondary" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onBlur={handleSearchBlur}
                    placeholder={t('header.searchPlaceholder')}
                    className="w-full bg-transparent outline-none text-sm text-foreground placeholder:text-foreground/70"
                  />
                </div>
              </form>
            </div>

            {/* User + icons or Close button when searching */}
            <div className="flex items-center gap-4 ml-4">
              {isSearching ? (
                <button
                  type="button"
                  className="text-secondary hover:text-primary-text transition-colors cursor-pointer ml-auto"
                  onClick={() => setSearchQuery('')}
                  title={t('header.closeSearch')}
                  aria-label={t('header.closeSearch')}
                >
                  <X className="w-7 h-7" />
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="flex items-center gap-2 text-sm text-foreground hover:text-secondary transition-colors cursor-pointer"
                    onClick={handleAccountClick}
                    title={accountButtonText}
                    aria-label={accountButtonText}
                  >
                    <span className="inline-flex items-center justify-center rounded-full bg-secondary/5 w-8 h-8 overflow-hidden">
                      <UserCircle2 className="w-7 h-7 text-secondary" />
                    </span>
                    <span className="hidden lg:inline">
                      {accountButtonLabel}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="text-secondary hover:text-primary-text transition-colors cursor-pointer"
                    onClick={() => handleNavClick('/events')}
                    title={t('header.events')}
                    aria-label={t('header.events')}
                  >
                    <CalendarDays className="w-6 h-6" />
                  </button>
                  <button
                    type="button"
                    className="text-secondary hover:text-primary-text transition-colors cursor-pointer"
                    onClick={() => handleNavClick(favoritesPath)}
                    title={t('header.favorites')}
                    aria-label={t('header.favorites')}
                  >
                    <Heart className="w-6 h-6" />
                  </button>
                  <button
                    type="button"
                    className="text-secondary hover:text-primary-text transition-colors cursor-pointer relative"
                    onClick={() => {
                      if (onNavigateToCart) {
                        onNavigateToCart()
                      } else {
                        handleNavClick('Carrinho')
                      }
                    }}
                    title={t('header.cart')}
                    aria-label={t('header.cart')}
                  >
                    <ShoppingBag className="w-6 h-6" />
                    {cartItemCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[1rem] px-1 h-4 bg-primary text-on-primary rounded-full text-xs flex items-center justify-center font-semibold">
                        {cartItemCount}
                      </span>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Mobile layout */}
          <div className="flex md:hidden flex-col gap-1">
            <div className={`flex items-baseline justify-between transition-all duration-300 ease-in-out ${isSearchFocused ? 'max-h-0 opacity-0 mb-0' : 'max-h-20 opacity-100 mb-3'}`}>
              {/* Menu icon + Logo grouped together */}
              <div className="flex items-baseline gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsNavOpen((open) => {
                      if (!open) {
                        setIsSearchFocused(false)
                      }
                      return !open
                    })
                  }}
                  className="text-primary-text hover:text-secondary transition-colors cursor-pointer"
                  title={t('header.openMenu')}
                  aria-label={t('header.openMenu')}
                >
                  <Menu className="w-7 h-7 -mb-1" />
                </button>

                <div className="text-foreground font-bold text-xl flex items-center gap-2 cursor-pointer">
                  <NavLink to="/" className="flex items-center gap-2 hover:text-secondary transition-colors cursor-pointer">
                    {!logoError ? (
                      <img
                        src={tenantTheme.logoUrl}
                        alt={`${tenantTheme.name} logo`}
                        className="h-7"
                        onError={() => setLogoError(true)}
                        title={`${tenantTheme.name} logo`}
                        width={140}
                        height={28}
                      />
                    ) : (
                      <span className="font-bold text-lg">{tenantTheme.name}</span>
                    )}
                  </NavLink>
                </div>
              </div>

              {/* Icons */}
              <div className="flex items-baseline gap-3">
                <button
                  type="button"
                  className="text-secondary hover:text-primary-text transition-colors cursor-pointer"
                  onClick={handleAccountClick}
                  title={t('header.profile')}
                  aria-label={t('header.profile')}
                >
                  <UserCircle2 className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  className="text-secondary hover:text-primary-text transition-colors cursor-pointer"
                  onClick={() => handleNavClick('/events')}
                  title={t('header.events')}
                  aria-label={t('header.events')}
                >
                  <CalendarDays className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  className="text-secondary hover:text-primary-text transition-colors cursor-pointer"
                  onClick={() => handleNavClick(favoritesPath)}
                  title={t('header.favorites')}
                  aria-label={t('header.favorites')}
                >
                  <Heart className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  className="text-secondary hover:text-primary-text transition-colors cursor-pointer relative"
                  onClick={() => {
                    if (onNavigateToCart) {
                      onNavigateToCart()
                    } else {
                      handleNavClick('Carrinho')
                    }
                  }}
                  title={t('header.cart')}
                  aria-label={t('header.cart')}
                >
                  <ShoppingBag className="w-6 h-6" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[1rem] px-1 h-4 bg-primary text-on-primary rounded-full text-xs flex items-center justify-center font-semibold">
                      {cartItemCount}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Mobile search */}
            <form onSubmit={handleSearchSubmit} className="flex-1">
              <div className="flex items-center gap-2 w-full rounded-2xl border border-secondary/20 bg-background px-4 py-3">
                {isSearchFocused ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsSearchFocused(false)
                      setSearchQuery('')
                    }}
                    className="text-secondary hover:text-primary-text transition-colors cursor-pointer"
                    title={t('header.back')}
                    aria-label={t('header.back')}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                ) : (
                  <Search className="w-5 h-5 text-secondary" />
                )}
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => {
                    setIsSearchFocused(true)
                    setIsNavOpen(false)
                  }}
                  onBlur={handleSearchBlur}
                  placeholder={t('header.searchPlaceholder')}
                  className="w-full bg-transparent outline-none text-sm text-foreground placeholder:text-foreground/70"
                />
                {isSearchFocused && searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="text-secondary/60 hover:text-secondary transition-colors cursor-pointer"
                    title={t('header.clearSearch')}
                    aria-label={t('header.clearSearch')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Navigation (desktop & mobile) - hidden on account page */}
          {!isAccountPage && (
            <Nav
              menuItems={mainMenuNavLinks}
              horizontalNavItems={horizontalMenuNavLinks}
              isNavOpen={isNavOpen}
              onNavClick={handleNavClick}
              setIsNavOpen={setIsNavOpen}
            />
          )}
        </Container>

        {/* Search results expanded in header bottom */}
        <div
          className={`absolute top-full md:top-[71px] pb-4 left-0 w-full bg-background border-t border-secondary/10 shadow-lg z-50 transition-all duration-300 ease-in-out origin-top ${shouldShowResults ? 'opacity-100 translate-y-0 visible' : 'opacity-0 -translate-y-2 invisible pointer-events-none'
            }`}
        >
          <Container className="py-4 flex flex-col h-full">
            <div className="flex-1 flex flex-col md:flex-row md:pb-3 gap-6 overflow-hidden">
              {/* Left column – search & categories */}
              <div className="w-full md:w-1/3 min-w-65 flex flex-col gap-6">
                {recentSearches.length > 0 && debouncedSearchQuery.trim().length === 0 && (
                  <section className="flex flex-col gap-3 md:max-w-sm">
                    <h2 className="text-sm md:text-base font-semibold opacity-80">{t('header.recentSearches')}</h2>
                    <ul className="flex flex-col gap-1">
                      {recentSearches.map((term, idx) => (
                        <li key={`recent-${idx}`}>
                          <button
                            type="button"
                            onClick={() => {
                              saveRecentSearch(term)
                              setSearchQuery('')
                              setIsSearchFocused(false)
                              navigate(`/search?q=${encodeURIComponent(term)}`)
                            }}
                            className="w-full flex items-center gap-3 text-left hover:bg-secondary/5 pl-2 rounded-full py-2 transition-colors cursor-pointer"
                          >
                            <Clock10 className="w-4 h-4 text-secondary/60" />
                            <span className="font-medium text-base">{term}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
                {totalSearchResults > 0 && (
                  <section className="flex flex-col gap-3 md:max-w-sm">
                    <h2 className="text-sm md:text-base font-semibold opacity-80">{t('header.categories')}</h2>
                    <ul className="flex flex-col gap-1 w-full">
                      {searchData?.search?.collections?.slice(0, 5).map((item, idx) => (
                        <li key={`${item.collection.id}-${idx}`} className="w-full">
                          <button
                            type="button"
                            onClick={() => {
                              setSearchQuery('')
                              setIsSearchFocused(false)
                              navigate(`/categories/${item.collection.slug}`)
                            }}
                            className="w-full flex items-center justify-between gap-3 text-left hover:bg-secondary/5 rounded-full px-3 py-2 transition-colors cursor-pointer"
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Tag className="min-w-4 w-4 h-4 text-primary-text shrink-0" />
                              <span className="font-medium text-base truncate">{item.collection.name}</span>
                            </div>
                            <span className="text-xs shrink-0 text-foreground/70">{item.count}</span>
                          </button>
                        </li>
                      ))}
                      {(!searchData?.search?.collections || searchData?.search?.collections?.length === 0) && !isSearchLoading && (
                        <li className="text-sm italic text-foreground/70 px-4">{t('header.noCategoriesFound')}</li>
                      )}
                    </ul>
                  </section>
                )}
              </div>
              {/* Right column – products */}
              <div className="flex-1 overflow-x-auto flex flex-col gap-3 pb-4">
                {totalSearchResults > 0 && (
                  <div className="flex items-center justify-between">
                    <h2 className="text-base font-semibold text-foreground/80">{t('header.results')}</h2>
                    {totalSearchResults > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          saveRecentSearch(debouncedSearchQuery)
                          setSearchQuery('')
                          setIsSearchFocused(false)
                          navigate(`/search?q=${encodeURIComponent(debouncedSearchQuery)}`)
                        }}
                        className="text-base font-medium text-primary-text hover:text-secondary transition-colors cursor-pointer"
                      >
                        {t('header.viewAllResults', { count: totalSearchResults })}
                      </button>
                    )}
                  </div>
                )}


                {isSearchLoading ? (
                  <div className="flex items-center justify-center h-40">
                    <span className="text-base italic text-foreground/70">{t('header.searching')}</span>
                  </div>
                ) : searchResults.length > 0 ? (
                  <div className="flex flex-col md:flex-row gap-3">
                    {searchResults.slice(0, 5).map((product) => {
                      return (
                        <button
                          key={product.productId}
                          type="button"
                          className="w-full md:min-w-50 md:max-w-55 flex md:flex-col md:gap-3 md:pb-3 bg-background rounded-2xl shadow-sm border border-secondary/20 overflow-hidden shrink-0 cursor-pointer hover:border-primary/60 transition-colors text-left"
                          onClick={() => {
                            setSearchQuery('')
                            setIsSearchFocused(false)
                            navigate(`/products/${product.slug}`)
                          }}
                        >
                          <div className="relative bg-secondary/5 w-24 md:w-full aspect-square md:aspect-4/5 flex items-center justify-center overflow-hidden shrink-0">
                            {product.productAsset?.preview ? (
                              <img
                                src={product.productAsset.preview}
                                alt={product.productName}
                                className="w-full h-full object-cover"
                                width={200}
                                height={250}
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-secondary/30">
                                <Handbag className="w-8 h-8 md:w-12 md:h-12" />
                              </div>
                            )}
                          </div>
                          <div className="px-4 space-y-1 flex-1 flex flex-col justify-center">
                            <p className="text-base font-semibold leading-snug line-clamp-2">{product.productName}</p>
                            <p className="text-xs tracking-wide text-foreground/70">{product.sku}</p>
                            {(() => {
                              const calculatedPrice = pricesIncludeTax
                                ? productPricesMap.mapWithTax.get(product.productId)
                                : productPricesMap.map.get(product.productId)
                              const fallbackPrice = pricesIncludeTax ? product.priceWithTax : product.price
                              const priceToDisplay = calculatedPrice !== undefined ? calculatedPrice : fallbackPrice;
                              if (priceToDisplay === 0) {
                                return <p className="text-xs font-semibold text-primary-text mt-1">{t('header.priceOnRequest')}</p>
                              }
                              return (
                                <p className="text-sm font-semibold mt-1 flex gap-1 items-center">
                                  {formatPrice(priceToDisplay, product.currencyCode || 'EUR')}
                                  {!pricesIncludeTax && (
                                    <span className="text-[11px] font-normal text-foreground/70"> {t('header.plusVat')}</span>
                                  )}
                                </p>
                              )
                            })()}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                ) : debouncedSearchQuery ? (
                  <div className="flex items-center justify-center h-40">
                    <span className="text-sm text-foreground/70">{t('header.noProductsFound')} <span className='text-foreground'>&quot;{debouncedSearchQuery}&quot;</span></span>
                  </div>
                ) : null}
              </div>
            </div>
          </Container>
        </div>
      </header>

      {/* Backdrop overlay */}
      <div
        className={`fixed inset-0 bg-black/60 z-40 transition-all duration-300 ${shouldShowResults || isNavOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}
        onClick={() => {
          if (shouldShowResults) {
            setSearchQuery('')
            setIsSearchFocused(false)
          }
          if (isNavOpen) {
            setIsNavOpen(false)
          }
        }}
        aria-hidden="true"
      />
    </>
  )
}
